// ---------------------------------------------------------------------------
// LES LANGUES DE SOUS-TITRES : CELLES QU'ON GARDE, CELLES QU'ON ECARTE
//
// Toji : « il faut que tu fasses un réglage pour afficher que ceux qui sont
// sélectionnés : français, anglais. Le reste, tu me les filtres
// automatiquement (…) une section avec les filtrés, et on peut voir la plupart
// des langues traitées par les sous-titres. »
//
// ⚠️ UNE LANGUE PORTE PLUSIEURS CODES, ET C'EST TOUT LE PROBLEME.
//
// Un fichier annonce le français « fre », « fra » ou « fr » selon l'outil qui
// l'a fabriqué — ce sont les deux normes ISO 639-2 (bibliographique et
// terminologique) plus la 639-1. Un menu qui propose les trois demande à la
// personne de connaître cette histoire ; un filtre qui n'en connaît qu'un en
// laisse passer deux tiers. On raisonne donc par LANGUE, et chacune porte tous
// ses codes.
//
// Le réglage, lui, reste une liste de codes à plat (« fre, fra, fr, eng, en ») :
// c'est ce que le reste du lecteur lit déjà, et ce qui voyage vers le serveur
// pour qu'il choisisse un fichier. On traduit ici, on n'impose rien ailleurs.
// ---------------------------------------------------------------------------

/**
 * Ce que les add-ons de sous-titres servent vraiment.
 *
 * La liste n'est pas l'ISO complète — sept mille langues dont personne n'a
 * besoin — mais ce qu'OpenSubtitles et ses semblables proposent réellement.
 * `codes` commence par le code le plus courant dans les fichiers.
 */
export const LANGUES_ST = Object.freeze([
  { cle: 'fr', nom: 'Français', codes: ['fre', 'fra', 'fr'] },
  { cle: 'en', nom: 'Anglais', codes: ['eng', 'en'] },
  { cle: 'ja', nom: 'Japonais', codes: ['jpn', 'ja', 'jp'] },
  { cle: 'es', nom: 'Espagnol', codes: ['spa', 'es'] },
  { cle: 'pt', nom: 'Portugais', codes: ['por', 'pt'] },
  { cle: 'pt-br', nom: 'Portugais (Brésil)', codes: ['pob', 'pt-br', 'pt-BR'] },
  { cle: 'de', nom: 'Allemand', codes: ['ger', 'deu', 'de'] },
  { cle: 'it', nom: 'Italien', codes: ['ita', 'it'] },
  { cle: 'nl', nom: 'Néerlandais', codes: ['dut', 'nld', 'nl'] },
  { cle: 'ru', nom: 'Russe', codes: ['rus', 'ru'] },
  { cle: 'pl', nom: 'Polonais', codes: ['pol', 'pl'] },
  { cle: 'ar', nom: 'Arabe', codes: ['ara', 'ar'] },
  { cle: 'zh', nom: 'Chinois', codes: ['chi', 'zho', 'zh', 'cmn'] },
  { cle: 'ko', nom: 'Coréen', codes: ['kor', 'ko'] },
  { cle: 'tr', nom: 'Turc', codes: ['tur', 'tr'] },
  { cle: 'sv', nom: 'Suédois', codes: ['swe', 'sv'] },
  { cle: 'da', nom: 'Danois', codes: ['dan', 'da'] },
  { cle: 'no', nom: 'Norvégien', codes: ['nor', 'no', 'nob'] },
  { cle: 'fi', nom: 'Finnois', codes: ['fin', 'fi'] },
  { cle: 'cs', nom: 'Tchèque', codes: ['cze', 'ces', 'cs'] },
  { cle: 'el', nom: 'Grec', codes: ['gre', 'ell', 'el'] },
  { cle: 'he', nom: 'Hébreu', codes: ['heb', 'he'] },
  { cle: 'hu', nom: 'Hongrois', codes: ['hun', 'hu'] },
  { cle: 'ro', nom: 'Roumain', codes: ['rum', 'ron', 'ro'] },
  { cle: 'id', nom: 'Indonésien', codes: ['ind', 'id'] },
  { cle: 'th', nom: 'Thaï', codes: ['tha', 'th'] },
  { cle: 'vi', nom: 'Vietnamien', codes: ['vie', 'vi'] },
  { cle: 'uk', nom: 'Ukrainien', codes: ['ukr', 'uk'] },
  { cle: 'hi', nom: 'Hindi', codes: ['hin', 'hi'] },
]);

/**
 * Ce que le réglage vaut quand personne n'y a touché : le FRANÇAIS, et lui seul.
 *
 * Toji : « de base, au niveau des sous-titres, on va fournir que les français.
 * Techniquement, les anglais sont dispo aussi, mais il faut aller dans les
 * réglages et les activer. »
 *
 * L'anglais reste à un clic — il est en tête des langues filtrées — mais il ne
 * s'impose à personne. Un menu qui propose deux langues quand on n'en lit
 * qu'une est déjà un menu de trop.
 */
export const LANGUES_PAR_DEFAUT = Object.freeze(['fr']);

const PAR_CODE = new Map();
for (const l of LANGUES_ST) {
  for (const c of l.codes) PAR_CODE.set(c.toLowerCase(), l);
}

/**
 * La langue d'un code de piste, ou `null`.
 *
 * ⚠️ Les fichiers portent des codes composés — « fr-FR », « pt_BR », « eng-SDH ».
 * On coupe au premier séparateur, puis on réessaie sur la chaîne entière : le
 * portugais brésilien, lui, EST un code composé, et le couper le rendrait
 * portugais tout court.
 */
export function langueDuCode(code) {
  const brut = String(code ?? '').trim().toLowerCase();
  if (!brut) return null;
  if (PAR_CODE.has(brut)) return PAR_CODE.get(brut);
  const tete = brut.split(/[-_.\s]/)[0];
  return PAR_CODE.get(tete) ?? null;
}

/** Tous les codes des langues retenues, à plat et dans l'ordre choisi. */
export function codesDesLangues(cles) {
  const vus = new Set();
  const codes = [];
  for (const cle of cles ?? []) {
    const l = LANGUES_ST.find((x) => x.cle === cle);
    if (!l) continue;
    for (const c of l.codes) {
      if (vus.has(c)) continue;
      vus.add(c);
      codes.push(c);
    }
  }
  return codes;
}

/**
 * Les langues retenues, retrouvées depuis la liste de codes enregistrée.
 *
 * L'ordre est celui des codes : c'est lui qui dit laquelle est prioritaire.
 */
export function clesDepuisCodes(codes) {
  const cles = [];
  for (const c of codes ?? []) {
    const l = langueDuCode(c);
    if (l && !cles.includes(l.cle)) cles.push(l.cle);
  }
  return cles;
}

/**
 * Ne garde que les pistes dont la langue est retenue.
 *
 * ⚠️ SI RIEN NE CORRESPOND, ON GARDE TOUT.
 *
 * Un épisode dont les seuls sous-titres sont en japonais ne doit pas se
 * retrouver SANS aucune piste parce qu'on a coché « français, anglais » : le
 * filtre existe pour ranger un menu de treize entrées, pas pour supprimer la
 * seule chose disponible. Le repli le dit à l'appelant (`filtre: false`), qui
 * peut alors l'expliquer plutôt que de laisser croire à un réglage ignoré.
 */
export function pistesRetenues(pistes, cles) {
  const liste = Array.isArray(pistes) ? pistes : [];
  const voulues = new Set(cles ?? []);
  if (!liste.length || !voulues.size) return { pistes: liste, filtre: false, ecartees: 0 };

  const gardees = liste.filter((p) => {
    const l = langueDuCode(p?.langue);
    return l ? voulues.has(l.cle) : false;
  });
  if (!gardees.length) return { pistes: liste, filtre: false, ecartees: 0 };

  // Dans l'ordre choisi : la première langue de la liste passe devant, et le
  // menu s'ouvre donc sur ce qu'on veut lire.
  const rang = (p) => {
    const l = langueDuCode(p?.langue);
    const i = l ? (cles ?? []).indexOf(l.cle) : -1;
    return i < 0 ? 999 : i;
  };
  gardees.sort((a, b) => rang(a) - rang(b));
  return { pistes: gardees, filtre: true, ecartees: liste.length - gardees.length };
}

/** Les langues effectivement présentes dans un lot de pistes. */
export function languesPresentes(pistes) {
  const vues = new Map();
  for (const p of pistes ?? []) {
    const l = langueDuCode(p?.langue);
    if (l) vues.set(l.cle, l);
  }
  return [...vues.values()];
}
